import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Toast } from "../components/common/Toast";
import { ACCESS_TOKEN, ADMIN_LOGOUT_URL, PROFILE } from "../helpers/Constant";
import api from "../services/Api";

export const AuthContext = createContext("AuthContext");

const getStoredAuthToken = () => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    return token && token !== "undefined" && token !== "null" ? token : "";
};

const ADMIN_ACTIVITY_KEY = "fastwin_admin_last_activity";

export const useAuth = () => {
    return useContext(AuthContext);
};

const AuthContextProvider = ({ children }) => {
    const storedToken = getStoredAuthToken();
    // A browser token is only a hint. The server must validate it before the
    // admin layout or any admin data request is rendered.
    const [isLogin, setIsLogin] = useState(false);
    const [authChecking, setAuthChecking] = useState(Boolean(storedToken));
    const [profile, setProfile] = useState(null);
    const [role, setRole] = useState();
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [twoFactorStage, setTwoFactorStage] = useState(null);
    const [twoFactorSetup, setTwoFactorSetup] = useState(null);
    const activityWriteRef = useRef(0);

    const syncProfile = (nextProfile) => {
        setProfile(nextProfile);

        if (nextProfile) {
            localStorage.setItem(PROFILE, JSON.stringify(nextProfile));
        } else {
            localStorage.removeItem(PROFILE);
        }
    };

    const login = async (body) => {
        api.auth.login({ setLoading, body }, (response) => {
            const nextToken = response?.data?.token;
            const nextUser = response?.data?.user;

            if (nextUser?.type === "Admin" && nextToken) {
                localStorage.setItem(ACCESS_TOKEN, nextToken);
                syncProfile(nextUser);
                setRole(nextUser.role);
                if (response.data.requiresTwoFactorSetup) {
                    setTwoFactorStage("setup");
                    setTwoFactorSetup(null);
                    setIsLogin(false);
                } else if (response.data.requiresTwoFactor) {
                    setTwoFactorStage("verify");
                    setTwoFactorSetup(null);
                    setIsLogin(false);
                } else {
                    setTwoFactorStage(null);
                    setIsLogin(true);
                }
                setAuthChecking(false);
            } else {
                localStorage.removeItem(ACCESS_TOKEN);
                syncProfile(null);
                setAuthChecking(false);
                Toast(
                    "error",
                    response?.data?.error ?? response?.data?.message ?? "Admin login failed",
                    ""
                );
            }
        });
    };

    const loadTwoFactorSetup = async () => {
        return api.auth.getTwoFactorSetup(setProfileLoading, (response) => {
            setTwoFactorSetup(response.data);
        });
    };

    const verifyTwoFactor = async (code) => {
        return api.auth.verifyTwoFactor({ code, setLoading }, (response) => {
            const nextToken = response?.data?.token;
            const nextUser = response?.data?.user;
            if (nextToken && nextUser?.type === "Admin") {
                localStorage.setItem(ACCESS_TOKEN, nextToken);
                syncProfile(nextUser);
                setRole(nextUser.role);
                setTwoFactorStage(null);
                setTwoFactorSetup(null);
                setIsLogin(true);
                setAuthChecking(false);
            }
        });
    };

    const getUserProfile = async () => {
        api.auth.getAdminSession(setProfileLoading, (response) => {
            if (response.data.type === "Admin") {
                syncProfile(response.data);
                setIsLogin(true);
                setRole(response.data.role);
                setAuthChecking(false);
            } else {
                Toast(
                    "error",
                    response.data.error ??
                        `User is not admin. Logged out from admin panel`,
                    ""
                );
                logout();
            }
        }, () => {
            setAuthChecking(false);
            setIsLogin(false);
            syncProfile(null);
        });
    };

    useEffect(() => {
        if (!storedToken) {
            setAuthChecking(false);
            return;
        }
        api.auth.getTwoFactorStatus(setProfileLoading, (response) => {
            const stage = response?.data?.stage;
            if (stage === "setup" || stage === "verify") {
                setTwoFactorStage(stage);
                setIsLogin(false);
                setAuthChecking(false);
            } else {
                setTwoFactorStage(null);
                getUserProfile();
            }
        }, () => {
            setAuthChecking(false);
            setIsLogin(false);
            setTwoFactorStage(null);
            syncProfile(null);
        });
    }, []);

    const logout = useCallback(() => {
        const token = getStoredAuthToken();
        setIsLogin(false);
        setAuthChecking(false);
        syncProfile(null);
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(PROFILE);
        setTwoFactorStage(null);
        setTwoFactorSetup(null);
        localStorage.removeItem(ADMIN_ACTIVITY_KEY);

        if (token) {
            fetch(ADMIN_LOGOUT_URL, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                keepalive: true
            }).catch(() => undefined);
        }
    }, []);

    useEffect(() => {
        if (!isLogin) return undefined;

        const timeoutMinutes = Math.max(
            5,
            Number(profile?.adminSession?.idleTimeoutMinutes) || 30
        );
        const timeoutMs = timeoutMinutes * 60 * 1000;
        const markActivity = () => {
            const now = Date.now();
            if (now - activityWriteRef.current < 15000) return;
            activityWriteRef.current = now;
            localStorage.setItem(ADMIN_ACTIVITY_KEY, String(now));
        };
        markActivity();

        const events = ["pointerdown", "pointermove", "keydown", "touchstart", "scroll"];
        events.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));

        const idleCheck = window.setInterval(() => {
            const lastActivity = Number(localStorage.getItem(ADMIN_ACTIVITY_KEY)) || 0;
            if (Date.now() - lastActivity >= timeoutMs) {
                Toast("warning", "Session ended", "You were signed out after a period of inactivity.");
                logout();
            }
        }, 15000);

        const heartbeat = window.setInterval(() => {
            const lastActivity = Number(localStorage.getItem(ADMIN_ACTIVITY_KEY)) || 0;
            if (lastActivity && Date.now() - lastActivity < 5 * 60 * 1000) {
                api.auth.getAdminSession(() => {}, () => {}, () => logout());
            }
        }, 4 * 60 * 1000);

        const syncTabs = (event) => {
            if (event.key === ACCESS_TOKEN && !event.newValue) {
                setIsLogin(false);
                syncProfile(null);
            }
        };
        window.addEventListener("storage", syncTabs);

        return () => {
            events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
            window.removeEventListener("storage", syncTabs);
            window.clearInterval(idleCheck);
            window.clearInterval(heartbeat);
        };
    }, [isLogin, profile?.adminSession?.idleTimeoutMinutes, logout]);

    return (
        <AuthContext.Provider
            value={{
                isLogin,
                authChecking,
                profile,
                loading,
                profileLoading,
                role,
                permissions: profile ? profile.permissions : [],
                login,
                logout,
                getUserProfile,
                setProfile: syncProfile,
                twoFactorStage,
                twoFactorSetup,
                loadTwoFactorSetup,
                verifyTwoFactor
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;
